import { useState } from "react";
import {
  Eye,
  EyeOff,
  Lock,
  Mail,
  ArrowLeft,
  User,
  Building,
  MapPin,
  Palette,
  CheckCircle2,
} from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { useSchoolProfile } from "../../app/SchoolProfileContext";

const COLORS = [
  "#1967d2",
  "#d93025",
  "#188038",
  "#f9ab00",
  "#e91e63",
  "#9c27b0",
  "#673ab7",
  "#3f51b5",
  "#009688",
  "#795548",
  "#607d8b",
  "#202124",
];

interface FormErrors {
  name?: string;
  email?: string;
  school?: string;
  password?: string;
  confirmPassword?: string;
  acceptTerms?: string;
  form?: string;
}

interface RegisterPageProps {
  onBackClick?: () => void;
  onRegisterSuccess?: () => void;
  onLoginClick?: () => void;
}

export default function RegisterPage({
  onRegisterSuccess,
  onLoginClick,
}: RegisterPageProps) {
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [school, setSchool] = useState<string>("");
  const [institution, setInstitution] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [schoolColor, setSchoolColor] = useState<string>("#1967d2");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { register } = useAuth();
  const [didSubmit, setDidSubmit] = useState<boolean>(false);
  const { addProfile } = useSchoolProfile();

  const getValidationErrors = (values: {
    name: string;
    email: string;
    school: string;
    institution: string;
    city: string;
    password: string;
    confirmPassword: string;
    acceptTerms: boolean;
  }): FormErrors => {
    const newErrors: FormErrors = {};

    if (!values.name || values.name.trim().length < 3) {
      newErrors.name = "Nome completo é obrigatório (mínimo 3 caracteres)";
    }

    if (!values.email) {
      newErrors.email = "Email é obrigatório";
    } else if (!/\S+@\S+\.\S+/.test(values.email)) {
      newErrors.email = "Email inválido";
    }

    if (!values.school || values.school.trim().length < 3) {
      newErrors.school = "Nome da escola é obrigatório";
    }

    if (!values.password) {
      newErrors.password = "Senha é obrigatória";
    } else if (values.password.length < 8) {
      newErrors.password = "Senha deve ter no mínimo 8 caracteres";
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(values.password)) {
      newErrors.password =
        "Senha deve conter letras maiúsculas, minúsculas e números";
    }

    if (!values.confirmPassword) {
      newErrors.confirmPassword = "Confirmação de senha é obrigatória";
    } else if (values.password !== values.confirmPassword) {
      newErrors.confirmPassword = "As senhas não coincidem";
    }

    if (!values.acceptTerms) {
      newErrors.acceptTerms = "Você precisa aceitar os termos para continuar";
    }

    return newErrors;
  };

  const validateForm = (): boolean => {
    const newErrors = getValidationErrors({
      name,
      email,
      school,
      institution,
      city,
      password,
      confirmPassword,
      acceptTerms,
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setDidSubmit(true);

    if (validateForm()) {
      setIsLoading(true);
      try {
        setErrors({});
        const success = await register(name, email, school, password);

        if (success) {
          // Automatically create school profile
          try {
            await addProfile({
              name: school,
              institution: institution || undefined,
              city: city || undefined,
              color: schoolColor,
            });
          } catch (schoolError) {
            console.error("Erro ao criar perfil da escola:", schoolError);
            // Do not fail registration if school is not created
          }

          if (onRegisterSuccess) onRegisterSuccess();
        } else {
          setErrors({
            form: "Conta criada, mas houve um erro no login automático. Tente fazer login manualmente.",
          });
          if (onLoginClick) onLoginClick();
        }
      } catch (error) {
        let errorMessage =
          "Não foi possível criar a conta. Verifique seus dados.";
        if (error instanceof Error) {
          errorMessage += ` Detalhe: ${error.message}`;
        }
        setErrors({ form: errorMessage });
      } finally {
        setIsLoading(false);
      }
    } else {
      setErrors(errors);
    }
  };

  const handleBackClick = () => {
    if (onLoginClick) {
      onLoginClick();
    }
  };

  return (
    <div className="min-h-screen bg-(--bg-secondary) flex items-center justify-center p-4">
      <button
        onClick={handleBackClick}
        className="fixed top-4 left-4 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-(--text-tertiary) transition-all duration-200 hover:text-(--text-primary) hover:bg-(--bg-accent) z-10"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Voltar</span>
      </button>

      <div className="w-full max-w-md lg:max-w-5xl">
        <div
          className="bg-(--card-bg) rounded-2xl shadow-lg border lg:flex lg:overflow-hidden"
          style={{ borderColor: "var(--border-color)" }}
        >
          {/* Logo Section - Desktop */}
          <div
            className="hidden lg:flex lg:w-5/12 lg:flex-col lg:items-center lg:justify-center lg:p-8"
            style={{ backgroundColor: "var(--bg-secondary)" }}
          >
            <div
              className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4"
              style={{ backgroundColor: "var(--primary-color)" }}
            >
              <div className="w-16 h-16 rounded-lg flex items-center justify-center">
                <User className="w-12 h-12 text-white" />
              </div>
            </div>

            {/* School Customization Fields */}
            <div className="w-full max-w-xs space-y-3 mb-8">
              <div>
                <label
                  htmlFor="institution"
                  className="block text-xs font-medium mb-1.5"
                  style={{ color: "var(--text-primary)" }}
                >
                  Nível de Ensino (Opcional)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building
                      className="h-4 w-4"
                      style={{ color: "var(--text-tertiary)" }}
                    />
                  </div>
                  <input
                    id="institution"
                    type="text"
                    value={institution}
                    onChange={(e) => setInstitution(e.target.value)}
                    disabled={isLoading}
                    className="block w-full pl-9 pr-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition disabled:opacity-50 placeholder-(--text-tertiary)"
                    style={{
                      borderColor: "var(--border-color)",
                      color: "var(--text-primary)",
                      backgroundColor: "var(--input-bg)",
                    }}
                    placeholder="Ex: Ensino Médio"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="city"
                  className="block text-xs font-medium mb-1.5"
                  style={{ color: "var(--text-primary)" }}
                >
                  Cidade (Opcional)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MapPin
                      className="h-4 w-4"
                      style={{ color: "var(--text-tertiary)" }}
                    />
                  </div>
                  <input
                    id="city"
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    disabled={isLoading}
                    className="block w-full pl-9 pr-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition disabled:opacity-50 placeholder-(--text-tertiary)"
                    style={{
                      borderColor: "var(--border-color)",
                      color: "var(--text-primary)",
                      backgroundColor: "var(--input-bg)",
                    }}
                    placeholder="Ex: São Paulo - SP"
                  />
                </div>
              </div>

              <div>
                <label
                  className="block text-xs font-medium mb-1.5"
                  style={{ color: "var(--text-primary)" }}
                >
                  Cor de Identificação
                </label>
                <div className="grid grid-cols-6 gap-2">
                  {COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setSchoolColor(color)}
                      className={`h-6 w-6 rounded flex items-center justify-center transition-all ${
                        schoolColor === color
                          ? "ring-2 ring-offset-1 ring-(--primary-color) scale-110"
                          : "hover:scale-105"
                      }`}
                      style={{ backgroundColor: color }}
                      disabled={isLoading}
                    >
                      {schoolColor === color && (
                        <CheckCircle2 className="w-3 h-3 text-white" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <h2
              className="text-xl font-bold mb-2"
              style={{ color: "var(--text-primary)" }}
            >
              Junte-se a nós
            </h2>
            <p
              className="text-center text-sm"
              style={{ color: "var(--text-tertiary)" }}
            >
              Crie sua conta e comece a usar nossa plataforma
            </p>
          </div>

          {/* Form Section */}
          <div className="p-6 lg:w-7/12 lg:p-8">
            {/* Header - Mobile */}
            <div className="text-center mb-6 lg:text-left">
              <div
                className="lg:hidden inline-flex items-center justify-center w-14 h-14 rounded-full mb-3"
                style={{ backgroundColor: "rgba(25, 103, 210, 0.1)" }}
              >
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: "var(--primary-color)" }}
                >
                  <User className="w-6 h-6 text-white" />
                </div>
              </div>
              <h1
                className="text-2xl font-bold mb-1"
                style={{ color: "var(--text-primary)" }}
              >
                Criar Conta
              </h1>
              <p className="text-sm" style={{ color: "var(--text-tertiary)" }}>
                Preencha seus dados para começar
              </p>
            </div>

            {/* Form */}
            <div className="space-y-3">
              {errors.form && (
                <div
                  className="px-3 py-2 rounded-lg text-xs border"
                  style={{
                    borderColor: "var(--danger-color)",
                    color: "var(--danger-color)",
                    backgroundColor: "var(--input-bg)",
                  }}
                >
                  {errors.form}
                </div>
              )}
              {/* Name Input */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-xs font-medium mb-1.5"
                  style={{ color: "var(--text-primary)" }}
                >
                  Nome Completo
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User
                      className="h-4 w-4"
                      style={{ color: "var(--text-tertiary)" }}
                    />
                  </div>
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => {
                      const nextName = e.target.value;
                      setName(nextName);
                      if (didSubmit) {
                        setErrors(
                          getValidationErrors({
                            name: nextName,
                            email,
                            school,
                            institution,
                            city,
                            password,
                            confirmPassword,
                            acceptTerms,
                          }),
                        );
                      }
                    }}
                    disabled={isLoading}
                    className="block w-full pl-9 pr-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition disabled:opacity-50 placeholder-(--text-tertiary)"
                    style={{
                      borderColor: errors.name
                        ? "var(--danger-color)"
                        : "var(--border-color)",
                      color: "var(--text-primary)",
                      backgroundColor: "var(--input-bg)",
                    }}
                    placeholder="João Silva"
                  />
                </div>
                {errors.name && (
                  <p
                    className="mt-1 text-xs"
                    style={{ color: "var(--danger-color)" }}
                  >
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Email Input */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-xs font-medium mb-1.5"
                  style={{ color: "var(--text-primary)" }}
                >
                  Email Institucional
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail
                      className="h-4 w-4"
                      style={{ color: "var(--text-tertiary)" }}
                    />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      const nextEmail = e.target.value;
                      setEmail(nextEmail);
                      if (didSubmit) {
                        setErrors(
                          getValidationErrors({
                            name,
                            email: nextEmail,
                            school,
                            institution,
                            city,
                            password,
                            confirmPassword,
                            acceptTerms,
                          }),
                        );
                      }
                    }}
                    disabled={isLoading}
                    className="block w-full pl-9 pr-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition disabled:opacity-50 placeholder-(--text-tertiary)"
                    style={{
                      borderColor: errors.email
                        ? "var(--danger-color)"
                        : "var(--border-color)",
                      color: "var(--text-primary)",
                      backgroundColor: "var(--input-bg)",
                    }}
                    placeholder="professor@escola.com.br"
                  />
                </div>
                {errors.email && (
                  <p
                    className="mt-1 text-xs"
                    style={{ color: "var(--danger-color)" }}
                  >
                    {errors.email}
                  </p>
                )}
              </div>

              {/* School Input */}
              <div>
                <label
                  htmlFor="school"
                  className="block text-xs font-medium mb-1.5"
                  style={{ color: "var(--text-primary)" }}
                >
                  Instituição de Ensino
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building
                      className="h-4 w-4"
                      style={{ color: "var(--text-tertiary)" }}
                    />
                  </div>
                  <input
                    id="school"
                    type="text"
                    value={school}
                    onChange={(e) => {
                      const nextSchool = e.target.value;
                      setSchool(nextSchool);
                      if (didSubmit) {
                        setErrors(
                          getValidationErrors({
                            name,
                            email,
                            school: nextSchool,
                            institution,
                            city,
                            password,
                            confirmPassword,
                            acceptTerms,
                          }),
                        );
                      }
                    }}
                    disabled={isLoading}
                    className="block w-full pl-9 pr-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition disabled:opacity-50 placeholder-(--text-tertiary)"
                    style={{
                      borderColor: errors.school
                        ? "var(--danger-color)"
                        : "var(--border-color)",
                      color: "var(--text-primary)",
                      backgroundColor: "var(--input-bg)",
                    }}
                    placeholder="Escola Municipal..."
                  />
                </div>
                {errors.school && (
                  <p
                    className="mt-1 text-xs"
                    style={{ color: "var(--danger-color)" }}
                  >
                    {errors.school}
                  </p>
                )}
              </div>

              {/* Passwords in Grid on Desktop */}
              <div className="lg:grid lg:grid-cols-2 lg:gap-3 space-y-3 lg:space-y-0">
                {/* Password Input */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-xs font-medium mb-1.5"
                    style={{ color: "var(--text-primary)" }}
                  >
                    Senha
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock
                        className="h-4 w-4"
                        style={{ color: "var(--text-tertiary)" }}
                      />
                    </div>
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        const nextPassword = e.target.value;
                        setPassword(nextPassword);
                        if (didSubmit) {
                          setErrors(
                            getValidationErrors({
                              name,
                              email,
                              school,
                              institution,
                              city,
                              password: nextPassword,
                              confirmPassword,
                              acceptTerms,
                            }),
                          );
                        }
                      }}
                      disabled={isLoading}
                      className="block w-full pl-9 pr-10 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition disabled:opacity-50 placeholder-(--text-tertiary)"
                      style={{
                        borderColor: errors.password
                          ? "var(--danger-color)"
                          : "var(--border-color)",
                        color: "var(--text-primary)",
                        backgroundColor: "var(--input-bg)",
                      }}
                      placeholder="Mínimo 8 caracteres"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      disabled={isLoading}
                    >
                      {showPassword ? (
                        <EyeOff
                          className="h-4 w-4 hover:opacity-75 transition"
                          style={{ color: "var(--text-tertiary)" }}
                        />
                      ) : (
                        <Eye
                          className="h-4 w-4 hover:opacity-75 transition"
                          style={{ color: "var(--text-tertiary)" }}
                        />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p
                      className="mt-1 text-xs"
                      style={{ color: "var(--danger-color)" }}
                    >
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* Confirm Password Input */}
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-xs font-medium mb-1.5"
                    style={{ color: "var(--text-primary)" }}
                  >
                    Confirmar Senha
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock
                        className="h-4 w-4"
                        style={{ color: "var(--text-tertiary)" }}
                      />
                    </div>
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => {
                        const nextConfirm = e.target.value;
                        setConfirmPassword(nextConfirm);
                        if (didSubmit) {
                          setErrors(
                            getValidationErrors({
                              name,
                              email,
                              school,
                              institution,
                              city,
                              password,
                              confirmPassword: nextConfirm,
                              acceptTerms,
                            }),
                          );
                        }
                      }}
                      disabled={isLoading}
                      className="block w-full pl-9 pr-10 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition disabled:opacity-50 placeholder-(--text-tertiary)"
                      style={{
                        borderColor: errors.confirmPassword
                          ? "var(--danger-color)"
                          : "var(--border-color)",
                        color: "var(--text-primary)",
                        backgroundColor: "var(--input-bg)",
                      }}
                      placeholder="Digite novamente"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? (
                        <EyeOff
                          className="h-4 w-4 hover:opacity-75 transition"
                          style={{ color: "var(--text-tertiary)" }}
                        />
                      ) : (
                        <Eye
                          className="h-4 w-4 hover:opacity-75 transition"
                          style={{ color: "var(--text-tertiary)" }}
                        />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p
                      className="mt-1 text-xs"
                      style={{ color: "var(--danger-color)" }}
                    >
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="pt-1">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => {
                      const nextAccept = e.target.checked;
                      setAcceptTerms(nextAccept);
                      if (didSubmit) {
                        setErrors(
                          getValidationErrors({
                            name,
                            email,
                            school,
                            institution,
                            city,
                            password,
                            confirmPassword,
                            acceptTerms: nextAccept,
                          }),
                        );
                      }
                    }}
                    disabled={isLoading}
                    className="h-4 w-4 mt-0.5 border-gray-300 rounded"
                    style={{ accentColor: "var(--primary-color)" }}
                  />
                  <span
                    className="ml-2 text-xs"
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    Aceito os{" "}
                    <a
                      href="#"
                      className="font-medium hover:opacity-75 transition"
                      style={{ color: "var(--primary-color)" }}
                    >
                      Termos de Uso
                    </a>{" "}
                    e a{" "}
                    <a
                      href="#"
                      className="font-medium hover:opacity-75 transition"
                      style={{ color: "var(--primary-color)" }}
                    >
                      Política de Privacidade
                    </a>
                  </span>
                </label>
                {errors.acceptTerms && (
                  <p
                    className="mt-1 text-xs"
                    style={{ color: "var(--danger-color)" }}
                  >
                    {errors.acceptTerms}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="w-full text-white py-2.5 px-4 rounded-lg text-sm font-semibold hover:shadow-lg focus:outline-none focus:ring-2 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: "var(--primary-color)" }}
              >
                {isLoading ? "Criando..." : "Criar Conta"}
              </button>
            </div>

            {/* Login Link */}
            <p
              className="mt-4 text-center lg:text-left text-xs"
              style={{ color: "var(--text-tertiary)" }}
            >
              Já tem uma conta?{" "}
              <button
                onClick={onLoginClick}
                disabled={isLoading}
                className="font-medium hover:opacity-75 transition"
                style={{ color: "var(--primary-color)" }}
              >
                Fazer Login
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
